import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES, buildProviderPatientPath } from "@/routes";
import { useQuery } from "@tanstack/react-query";
import {
  ClipboardList,
  Search,
  Filter,
  Plus,
  Eye,
  MessageSquare,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Clock,
  User,
  FileText,
  Phone,
  Mail,
} from "lucide-react";
import { ProviderSidebar } from "@/components/layout/ProviderSidebar";
import { ProviderHeader } from "@/components/layout/ProviderHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface PatientCase {
  id: string;
  patient_id: string;
  patient_name: string;
  patient_email: string | null;
  patient_phone: string;
  insurance_type: string;
  status: "active" | "pending" | "review" | "closed";
  priority: "high" | "medium" | "low";
  last_visit: string | null;
  next_appointment: string | null;
  open_tasks: number;
  notes_count: number;
}

// Case status configuration
const caseStatuses = {
  active: { label: "Active", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700", icon: Clock },
  review: { label: "Needs Review", color: "bg-red-100 text-red-700", icon: AlertCircle },
  closed: { label: "Closed", color: "bg-muted text-muted-foreground", icon: CheckCircle2 },
};

const priorityConfig = {
  high: { label: "High", color: "bg-red-100 text-red-700 border-red-200" },
  medium: { label: "Medium", color: "bg-amber-100 text-amber-700 border-amber-200" },
  low: { label: "Low", color: "bg-blue-100 text-blue-700 border-blue-200" },
};

export default function CaseManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedCase, setSelectedCase] = useState<PatientCase | null>(null);
  const [newNote, setNewNote] = useState("");
  const [isAddingNote, setIsAddingNote] = useState(false);

  // TODO Serg: review this logic
  // Fetch patient cases from telehealth sessions and intake forms
  const {
    data: cases,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["patient-cases", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      // Get assigned member IDs
      const { data: assignments, error: assignError } = await supabase
        .from("provider_members" as any)
        .select("member_id")
        .eq("provider_id", user.id);
      if (assignError) throw assignError;
      const assignedMemberIds = (assignments || []).map((a: any) => a.member_id);
      if (assignedMemberIds.length === 0) return [];

      // Get sessions for assigned members only
      const { data: sessions, error: sessionsError } = await supabase
        .from("telehealth_sessions")
        .select(
          `
          id, member_id, scheduled_date, scheduled_time, status, visit_type, visit_reason,
          member_profile:profiles!telehealth_sessions_member_id_profiles_fkey(display_name)
        `,
        )
        .in("member_id", assignedMemberIds)
        .order("scheduled_date", { ascending: false });

      if (sessionsError) throw sessionsError;

      // Get unique member IDs
      const memberIds = [...new Set(sessions?.map((s) => s.member_id) || [])];
      if (memberIds.length === 0) return [];

      // Batch fetch intake forms and session notes in parallel
      const [intakeResult, notesResult, profilesResult] = await Promise.all([
        supabase.from("patients").select("*").in("user_id", memberIds),
        supabase.from("session_notes").select("session_id"),
        supabase.from("profiles").select("id, display_name, email, first_name, last_name").in("id", memberIds),
      ]);

      const intakeForms = intakeResult.data;
      if (intakeResult.error) throw intakeResult.error;
      const notesData = notesResult.data;
      if (notesResult.error) throw notesResult.error;
      const profilesData = profilesResult.data;
      if (profilesResult.error) throw profilesResult.error;

      // Build case objects
      const patientCases: PatientCase[] = memberIds.map((memberId) => {
        const patientSessions = sessions?.filter((s) => s.member_id === memberId) || [];
        const intake = intakeForms?.find((i) => i.user_id === memberId);
        const profile = profilesData?.find((p) => p.id === memberId);
        const patientSessionIds = patientSessions.map((s) => s.id);
        const notesCount = notesData?.filter((n) => patientSessionIds.includes(n.session_id)).length || 0;

        // Determine case status based on sessions
        const hasUpcoming = patientSessions.some(
          (s) => s.status === "scheduled" && new Date(s.scheduled_date) >= new Date(),
        );
        const hasInProgress = patientSessions.some((s) => s.status === "in_progress" || s.status === "waiting");
        const lastSession = patientSessions[0];

        let status: PatientCase["status"] = "active";
        if (!hasUpcoming && !hasInProgress) {
          status = "review";
        } else if (hasInProgress) {
          status = "active";
        } else if (hasUpcoming) {
          status = "pending";
        }

        // Determine priority based on various factors
        let priority: PatientCase["priority"] = "medium";
        if (intake?.chronic_conditions || intake?.allergies) {
          priority = "high";
        } else if (!hasUpcoming && !hasInProgress) {
          priority = "high";
        }

        const upcomingSession = patientSessions.find(
          (s) => s.status === "scheduled" && new Date(s.scheduled_date) >= new Date(),
        );

        return {
          id: memberId,
          patient_id: memberId,
          patient_name: intake
            ? `${intake.first_name} ${intake.last_name}`
            : profile?.display_name ||
              (profile?.first_name && profile?.last_name
                ? `${profile.first_name} ${profile.last_name}`
                : "Unknown Patient"),
          patient_email: intake?.email || profile?.email || null,
          patient_phone: intake?.phone || "",
          insurance_type: intake?.insurance_type || "unknown",
          status,
          priority,
          last_visit: lastSession?.scheduled_date || null,
          next_appointment: upcomingSession
            ? `${upcomingSession.scheduled_date} ${upcomingSession.scheduled_time}`
            : null,
          open_tasks: hasUpcoming ? 0 : 1, // Simplified task count
          notes_count: notesCount,
        };
      });

      return patientCases;
    },
    enabled: !!user?.id,
  });

  // Filter cases
  const filteredCases =
    cases?.filter((c) => {
      const matchesSearch =
        c.patient_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.patient_email?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || c.status === statusFilter;
      const matchesPriority = priorityFilter === "all" || c.priority === priorityFilter;
      return matchesSearch && matchesStatus && matchesPriority;
    }) || [];

  // Stats
  const stats = {
    total: cases?.length || 0,
    active: cases?.filter((c) => c.status === "active").length || 0,
    needsReview: cases?.filter((c) => c.status === "review").length || 0,
    highPriority: cases?.filter((c) => c.priority === "high").length || 0,
  };

  const handleAddNote = async () => {
    if (!selectedCase || !newNote.trim() || !user) return;

    setIsAddingNote(true);

    // For now, we'll use the audit log to store case notes
    // In production, you'd want a dedicated case_notes table
    try {
      const { error } = await supabase.rpc("log_audit_event", {
        p_action: "case_note_added",
        p_resource_type: "case",
        p_resource_id: selectedCase.id,
        p_patient_id: selectedCase.patient_id,
        p_details: { note: newNote },
      });

      if (error) throw error;

      toast({
        title: "Note added",
        description: "Your case note has been saved.",
      });
      setNewNote("");
      refetch();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add note. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAddingNote(false);
    }
  };

  const getStatusBadge = (status: PatientCase["status"]) => {
    const config = caseStatuses[status];
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: PatientCase["priority"]) => {
    const config = priorityConfig[priority];
    return (
      <Badge variant="outline" className={config.color}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <ProviderSidebar />
      <div className="ml-64">
        <ProviderHeader />
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3">
                <ClipboardList className="h-8 w-8 text-primary" />
                Case Management
              </h1>
              <p className="mt-1 text-muted-foreground">Track patient cases, add notes, and coordinate care</p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Cases</p>
                    <p className="text-2xl font-bold">{stats.total}</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <ClipboardList className="h-5 w-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Cases</p>
                    <p className="text-2xl font-bold">{stats.active}</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Needs Review</p>
                    <p className="text-2xl font-bold">{stats.needsReview}</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">High Priority</p>
                    <p className="text-2xl font-bold">{stats.highPriority}</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters and Search */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="relative flex-1 min-w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search patients by name or email..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="review">Needs Review</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Cases Table */}
          <Card>
            <CardHeader>
              <CardTitle>Patient Cases</CardTitle>
              <CardDescription>
                {filteredCases.length} case{filteredCases.length !== 1 ? "s" : ""} found
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              ) : filteredCases.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No cases found</p>
                  <p className="text-sm">Cases are created when patients schedule appointments</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patient</TableHead>
                      <TableHead>Insurance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Last Visit</TableHead>
                      <TableHead>Next Appt</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCases.map((patientCase) => (
                      <TableRow key={patientCase.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarFallback className="bg-primary/10 text-primary text-sm">
                                {patientCase.patient_name
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{patientCase.patient_name}</p>
                              <p className="text-xs text-muted-foreground">{patientCase.patient_phone}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {patientCase.insurance_type.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(patientCase.status)}</TableCell>
                        <TableCell>{getPriorityBadge(patientCase.priority)}</TableCell>
                        <TableCell>
                          {patientCase.last_visit ? format(new Date(patientCase.last_visit), "MMM d, yyyy") : "—"}
                        </TableCell>
                        <TableCell>
                          {patientCase.next_appointment ? (
                            format(new Date(patientCase.next_appointment.split(" ")[0]), "MMM d, yyyy")
                          ) : (
                            <span className="text-muted-foreground">None scheduled</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span>{patientCase.notes_count}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" onClick={() => setSelectedCase(patientCase)}>
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogHeader>
                                <DialogTitle className="flex items-center gap-3">
                                  <Avatar className="h-10 w-10">
                                    <AvatarFallback className="bg-primary/10 text-primary">
                                      {patientCase.patient_name
                                        .split(" ")
                                        .map((n) => n[0])
                                        .join("")}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <span>{patientCase.patient_name}</span>
                                    <div className="flex items-center gap-2 mt-1">
                                      {getStatusBadge(patientCase.status)}
                                      {getPriorityBadge(patientCase.priority)}
                                    </div>
                                  </div>
                                </DialogTitle>
                                <DialogDescription>Case details and care coordination</DialogDescription>
                              </DialogHeader>

                              <Tabs defaultValue="overview" className="mt-4">
                                <TabsList className="grid w-full grid-cols-3">
                                  <TabsTrigger value="overview">Overview</TabsTrigger>
                                  <TabsTrigger value="notes">Notes</TabsTrigger>
                                  <TabsTrigger value="coordination">Care Coord</TabsTrigger>
                                </TabsList>

                                <TabsContent value="overview" className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label className="text-muted-foreground">Contact</Label>
                                      <div className="space-y-1">
                                        <div className="flex items-center gap-2 text-sm">
                                          <Phone className="h-4 w-4 text-muted-foreground" />
                                          {patientCase.patient_phone}
                                        </div>
                                        {patientCase.patient_email && (
                                          <div className="flex items-center gap-2 text-sm">
                                            <Mail className="h-4 w-4 text-muted-foreground" />
                                            {patientCase.patient_email}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="space-y-2">
                                      <Label className="text-muted-foreground">Insurance</Label>
                                      <p className="text-sm capitalize">
                                        {patientCase.insurance_type.replace("_", " ")}
                                      </p>
                                    </div>
                                    <div className="space-y-2">
                                      <Label className="text-muted-foreground">Last Visit</Label>
                                      <p className="text-sm">
                                        {patientCase.last_visit
                                          ? format(new Date(patientCase.last_visit), "MMMM d, yyyy")
                                          : "No previous visits"}
                                      </p>
                                    </div>
                                    <div className="space-y-2">
                                      <Label className="text-muted-foreground">Next Appointment</Label>
                                      <p className="text-sm">
                                        {patientCase.next_appointment
                                          ? format(new Date(patientCase.next_appointment.split(" ")[0]), "MMMM d, yyyy")
                                          : "None scheduled"}
                                      </p>
                                    </div>
                                  </div>

                                  <Button
                                    className="w-full mt-4"
                                    onClick={() => navigate(buildProviderPatientPath(patientCase.patient_id))}
                                  >
                                    <User className="h-4 w-4 mr-2" />
                                    View Full Patient Profile
                                  </Button>
                                </TabsContent>

                                <TabsContent value="notes" className="space-y-4">
                                  <div className="space-y-2">
                                    <Label>Add Case Note</Label>
                                    <Textarea
                                      placeholder="Enter your case note..."
                                      value={newNote}
                                      onChange={(e) => setNewNote(e.target.value)}
                                      rows={3}
                                    />
                                    <Button
                                      onClick={handleAddNote}
                                      disabled={!newNote.trim() || isAddingNote}
                                      size="sm"
                                    >
                                      <Plus className="h-4 w-4 mr-1" />
                                      {isAddingNote ? "Adding..." : "Add Note"}
                                    </Button>
                                  </div>
                                  <div className="border-t pt-4">
                                    <p className="text-sm text-muted-foreground mb-2">
                                      {patientCase.notes_count} session note{patientCase.notes_count !== 1 ? "s" : ""}{" "}
                                      on file
                                    </p>
                                    <Button variant="outline" size="sm">
                                      <FileText className="h-4 w-4 mr-1" />
                                      View Session Notes
                                    </Button>
                                  </div>
                                </TabsContent>

                                <TabsContent value="coordination" className="space-y-4">
                                  <div className="rounded-lg border p-4">
                                    <h4 className="font-medium mb-2">Care Team Tasks</h4>
                                    {patientCase.open_tasks > 0 ? (
                                      <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-sm p-2 bg-amber-50 rounded border border-amber-200">
                                          <AlertCircle className="h-4 w-4 text-amber-600" />
                                          <span>Schedule follow-up appointment</span>
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-sm text-muted-foreground">No open tasks</p>
                                    )}
                                  </div>
                                  <div className="rounded-lg border p-4">
                                    <h4 className="font-medium mb-2">Quick Actions</h4>
                                    <div className="flex flex-wrap gap-2">
                                      <Button variant="outline" size="sm">
                                        <Calendar className="h-4 w-4 mr-1" />
                                        Schedule Appointment
                                      </Button>
                                      <Button variant="outline" size="sm">
                                        <MessageSquare className="h-4 w-4 mr-1" />
                                        Send Message
                                      </Button>
                                      <Button variant="outline" size="sm">
                                        <FileText className="h-4 w-4 mr-1" />
                                        View Intake Form
                                      </Button>
                                    </div>
                                  </div>
                                </TabsContent>
                              </Tabs>
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
}
